import * as React from "react";
import Paper from "@mui/material/Paper";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import moment from "moment";
import Select from "@mui/material/Select";
import { Button, Divider, Pagination, PaginationItem } from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import API from "../../Api/api";
import toast from "react-hot-toast";
import { message } from "antd";

const columns = [
  { field: "asset_pool", headerName: "Location", minWidth: 250 },
  {
    field: "cost",
    headerName: "Cost",
    minWidth: 170,
    align: "left",
    format: (value) =>
      value.toLocaleString("en-US", {
        miniFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    field: "accum_depreciation",
    headerName: "Accumulated Depreciation",
    minWidth: 170,
    align: "left",
    format: (value) =>
      value.toLocaleString("en-US", {
        miniFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    field: "carrying_amount",
    headerName: "Carrying Amount",
    minWidth: 170,
    align: "left",
  },
  {
    field: "depreciation",
    headerName: "Depreciation",
    minWidth: 170,
    align: "left",
  },
];

export default function BranchesAllAssetSummaryReport() {
  const mode_state = useSelector((state) => state.mode);
  const [mode, setMode] = React.useState(mode_state);
  React.useEffect(() => {
    setMode(mode_state);
  }, [mode_state]);
  function createData(
    asset_pool,
    cost,
    accum_depreciation,
    carrying_amount,
    depreciation
  ) {
    return {
      asset_pool,
      cost,
      accum_depreciation,
      carrying_amount,
      depreciation,
    };
  }
  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        sx={{ bgcolor: "#ffffff" }}
        variant="outlined"
        shape="rounded"
        page={page + 1}
        count={pageCount}
        // @ts-expect-error
        renderItem={(props2) => <PaginationItem {...props2} disableRipple />}
        onChange={(event, value) => apiRef.current.setPage(value - 1)}
      />
    );
  }
  const assetsData = useSelector((state) => state.assets);
  const categoryData = useSelector((state) => state.category);
  const locationData = useSelector((state) => state.location);
  const [filterData, setFilterData] = React.useState({
    location: "",
    category: "",
    depreciation_start: "",
    depreciation_end: "",
    acquisition_start: "",
    acquisition_end: "",
    expiration_start: "",
    expiration_end: "",
  });
  const now = moment();
  const depStart = filterData.depreciation_start
    ? moment(filterData.depreciation_start)
    : now;

  const filteredData = assetsData.filter((item) => {
    if (item.category === "right-of-use asset") {
      return moment(item.exhaustion_date) >= depStart;
    }
    return true; // keep all other categories
  });
  const [data, setData] = React.useState(filteredData);

  function clearFilter() {
    setFilterData({
      location: "",
      category: "",
      depreciation_start: "",
      depreciation_end: "",
      acquisition_start: "",
      acquisition_end: "",
      expiration_start: "",
      expiration_end: "",
    });
    setData(filteredData);
  }

  const location = filterData.location
    ? `&location=${filterData.location.toLocaleLowerCase()}`
    : "";
  const cat = filterData.category
    ? `&category=${filterData.category.toLocaleLowerCase()}`
    : "";
  const purchase_date_start = filterData.acquisition_start
    ? `&purchase_date[gte]=${filterData.acquisition_start}`
    : "";
  const purchase_date_end = filterData.acquisition_end
    ? `&purchase_date[lte]=${filterData.acquisition_end}`
    : "";
  const expiration_start = filterData.expiration_start
    ? `&exhaustion_date[gte]=${filterData.expiration_start}`
    : "";
  const expiration_end = filterData.expiration_end
    ? `&exhaustion_date[lte]=${filterData.expiration_end}`
    : "";
  const depreciation_end = filterData.depreciation_end
    ? `&depreciation_end=${filterData.depreciation_end}`
    : "";
  const depreciation_start = filterData.depreciation_start
    ? `&depreciation_start=${filterData.depreciation_start}`
    : "";

  const query = `/api/v1/company-assets/get-asset-by-code/code?disposed=false&approved=true${cat}${location}${cat}${purchase_date_end}${purchase_date_start}${expiration_start}${expiration_end}${depreciation_end}${depreciation_start}`;

  // ! A function to fetch queried data from the database//---------start------------
  const fetchAsset = async () => {
    let fetchingId;
    try {
      if (
        !location &&
        !cat &&
        !purchase_date_end &&
        !purchase_date_start &&
        !expiration_end &&
        !expiration_start &&
        !depreciation_end &&
        !depreciation_start
      ) {
        toast.error("you can't generate  a report while all fields are empty");
        return;
      }
      if (depreciation_end && !depreciation_start) {
        toast.error(
          "both depreciation end and depreciation start are required"
        );
        return;
      }
      if (!depreciation_end && depreciation_start) {
        toast.error(
          "both depreciation end and depreciation start are required"
        );
        return;
      }
      if (moment(depreciation_end) < moment(depreciation_start)) {
        toast.error(
          "depreciation end date can't be less than depreciation start date"
        );
        return;
      }
      fetchingId = toast.loading("generating report please wait...");
      const res = await API.get(query);
      toast.remove(fetchingId);
      if (res.data.length === 0) {
        message.info("No data was found");
        return;
      }
      if (res.data.length > 0) {
        const filteredData__ = res.data.filter((item) => {
          if (item.category === "right-of-use asset") {
            return moment(item.exhaustion_date) >= depStart;
          }
          return true; // keep all other categories
        });
        setData(filteredData__);
        message.info("Reported successfully generated");
      }
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
    }
  };

  const handleChangeCategory = (event) => {
    setFilterData({
      ...filterData,
      category: event.target.value,
    });
  };
  const handleChangeLocation = (event) => {
    setFilterData({
      ...filterData,
      location: event.target.value,
    });
  };

  let currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
  });

  function filterAndCalculate(assets) {
    // Group and calculate costs by category
    const groupedData = assets.reduce((acc, asset) => {
      const location = asset.location;

      // Initialize location if not present
      if (!acc[location]) {
        acc[location] = {
          cost: 0,
          accum_depre: 0,
          carrying_amount: 0,
          depreciation: 0,
        };
      }

      acc[location].cost += asset.cost;
      acc[location].accum_depre += asset.accum_depre;
      acc[location].carrying_amount += asset.carrying_amount;
      acc[location].depreciation += asset.depreciation;

      return acc;
    }, {});

    return groupedData;
  }

  // Example: Calculate costs between 2023-01-01 and 2023-12-31
  const result = filterAndCalculate(data);
  const rows2 = Object.entries(result).map(([location, data]) =>
    createData(
      location,
      currencyFormatter.format(data.cost),
      currencyFormatter.format(data.accum_depre),
      currencyFormatter.format(data.carrying_amount),
      currencyFormatter.format(data.depreciation)
    )
  );

  // Optionally, add the total row
  const totalData = {
    cost: Object.values(result).reduce((sum, item) => sum + item.cost, 0),
    accum_depre: Object.values(result).reduce(
      (sum, item) => sum + item.accum_depre,
      0
    ),
    carrying_amount: Object.values(result).reduce(
      (sum, item) => sum + item.carrying_amount,
      0
    ),
    depreciation: Object.values(result).reduce(
      (sum, item) => sum + item.depreciation,
      0
    ),
  };

  rows2.push(
    createData(
      "Total",
      currencyFormatter.format(totalData.cost),
      currencyFormatter.format(totalData.accum_depre),
      currencyFormatter.format(totalData.carrying_amount),
      currencyFormatter.format(totalData.depreciation)
    )
  );
  return (
    <div style={{ height: "84vh" }}>
      <div
        style={{
          fontSize: "14px",
          marginBottom: "20px",
          display: "flex",
          flexDirection: "row",
          // alignItems: "center",
          justifyContent: "space-between",
          padding: "0px 5px 5px 0px",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", rowGap: 9 }}>
          <FormControl size="small" sx={{ m: 1, width: 140 }}>
            <InputLabel
              style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}
              id="demo-simple-select-standard-label"
            >
              Location
            </InputLabel>
            <Select
              style={{ backgroundColor: "#ccc" }}
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={filterData.location}
              onChange={handleChangeLocation}
              label="Category"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {locationData?.map((data) => {
                return (
                  <MenuItem key={data.id} value={data.name}>
                    {data.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ m: 1, width: 140 }}>
            <InputLabel
              style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}
              id="demo-simple-select-standard-label"
            >
              Category
            </InputLabel>
            <Select
              style={{ backgroundColor: "#ccc" }}
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={filterData.category}
              onChange={handleChangeCategory}
              label="Category"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {categoryData?.map((data) => {
                return (
                  <MenuItem key={data.id} value={data.asset_pool}>
                    {data.asset_pool}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor="start-date"
          >
            Depreciation start
          </label>
          <input
            value={filterData.depreciation_start}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                depreciation_start: e.target.value,
              })
            }
            style={{
              padding: "2px",
              borderRadius: "5px",

              border: "1px solid #3D4F58",
            }}
            id="start-date"
            type="date"
            size={"large"}
            name="start-date"
          />

          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor="end-date"
          >
            Depreciation end
          </label>
          <input
            value={filterData.depreciation_end}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                depreciation_end: e.target.value,
              })
            }
            style={{
              padding: "2px",
              borderRadius: "5px",

              border: "1px solid #3D4F58",
            }}
            id="end-date"
            type="date"
            size="small"
            name="end-date"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor="acq-start-date"
          >
            Acquisition start
          </label>
          <input
            value={filterData.acquisition_start}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                acquisition_start: e.target.value,
              })
            }
            style={{
              padding: "2px",
              borderRadius: "5px",

              border: "1px solid #3D4F58",
            }}
            id="acq-start-date"
            type="date"
            size="small"
          />
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor="acq-start-date"
          >
            Acquisition end
          </label>
          <input
            value={filterData.acquisition_end}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                acquisition_end: e.target.value,
              })
            }
            style={{
              padding: "2px",
              borderRadius: "5px",

              border: "1px solid #3D4F58",
            }}
            id="acq-end-date"
            type="date"
            size="small"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor="acq-start-date"
          >
            Expiration start
          </label>
          <input
            value={filterData.expiration_start}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                expiration_start: e.target.value,
              })
            }
            style={{
              padding: "2px",
              borderRadius: "5px",

              border: "1px solid #3D4F58",
            }}
            id="acq-end-date"
            type="date"
            size="small"
          />
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor="acq-start-date"
          >
            Expiration end
          </label>
          <input
            value={filterData.expiration_end}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                expiration_end: e.target.value,
              })
            }
            style={{
              padding: "2px",
              borderRadius: "5px",

              border: "1px solid #3D4F58",
            }}
            id="acq-end-date"
            type="date"
            size="small"
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: "0px 5px 5px 5px",
        }}
      >
        <Button
          onClick={fetchAsset}
          size="small"
          variant="contained"
          style={{
            backgroundColor: `${mode === "dark" ? "#0883FF" : "#116149"}`,
          }}
        >
          Generate Report
        </Button>
        <Button
          onClick={clearFilter}
          size="small"
          variant="contained"
          style={{ backgroundColor: "#f47373" }}
        >
          Clear Filter
        </Button>
      </div>
      <Divider style={{ marginBottom: "10px" }} />
      <Paper sx={{ width: "100%" }}>
        <DataGrid
          columns={columns}
          getRowId={(row) => row.asset_pool}
          rows={rows2}
          {...rows2}
          slots={{ toolbar: GridToolbar, pagination: CustomPagination }}
          checkboxSelection
          disableRowSelectionOnClick
          density="compact"
          style={{
            backgroundColor: `${mode === "dark" ? "#101418" : "#ffffff"}`,
            color: `${mode === "dark" ? "#fff" : "#000"}`,
          }}
          sx={{
            boxShadow: 2,
            border: 1,
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-footerCell": {
              backgroundColor: "primary.main",
            },
            "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
              backgroundColor: mode === "light" ? "#fafafa" : "#1d1d1d",
              borderBottom: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
              borderRight: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-cell": {
              color:
                mode === "light" ? "rgba(0,0,0,.85)" : "rgba(255,255,255,0.65)",
            },
          }}
          // style={{ height: "50vh" }}
        />
      </Paper>
    </div>
  );
}
